import React, { useState, useEffect } from 'react';
import en from '../../../translations/en.json';
import { toast } from '../../../components/shared/toast';
import { validateDistributorEmail } from '../utils/distributorValidation';
import './DistributionForm.css';

function DistributionForm({ onSave, onCancel, isModal = false, existingEmails = [], distributor }) {
  const [email, setEmail] = useState(distributor ? distributor.email : '');
  const [company, setCompany] = useState(distributor ? distributor.company : '');
  const [percentage, setPercentage] = useState(distributor && distributor.percentage !== undefined ? String(distributor.percentage) : '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  useEffect(() => {
    if (distributor) {
      setEmail(distributor.email || '');
      setCompany(distributor.company || '');
      setPercentage(distributor.percentage !== undefined ? String(distributor.percentage) : '');
    } else {
      setEmail('');
      setCompany('');
      setPercentage('');
    }
  }, [distributor]);

  const handleReset = () => {
    setEmail('');
    setCompany('');
    setPercentage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!email.trim()) {
      toast.warning(t('distributor.validationEmailRequired') || 'Email is required');
      return;
    }
    // simple email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailTrimmed = email.trim();
    if (!emailRegex.test(emailTrimmed)) {
      toast.warning(t('distributor.validationEmailInvalid') || 'Please enter a valid email address');
      return;
    }

    if (!company.trim()) {
      toast.warning(t('distributor.validationCompanyRequired') || 'Company name is required');
      return;
    }

    if (!percentage.trim()) {
      toast.warning(t('distributor.validationPercentageRequired') || 'Percentage is required');
      return;
    }

    const percentageNum = parseFloat(percentage);
    if (isNaN(percentageNum) || percentageNum < 0 || percentageNum > 100) {
      toast.warning(t('distributor.validationPercentageRange') || 'Percentage must be between 0 and 100');
      return;
    }

    const duplicateEmailMessage = validateDistributorEmail(existingEmails, emailTrimmed, distributor);
    if (duplicateEmailMessage) {
      toast.warning(duplicateEmailMessage);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        email: emailTrimmed,
        company: company.trim(),
        percentage: percentageNum
      };
      await onSave?.(payload);
    } catch (err) {
      toast.error(err.message || 'Failed to save distributor.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="distribution-form-wrapper">
      {/* Page Header (Only visible if not inside modal) */}
      {!isModal && (
        <div className="distribution-page-header">
          {/* Breadcrumb Navigation */}
          <nav className="breadcrumb-nav">
            <button type="button" className="breadcrumb-link" onClick={onCancel}>
              {t('distributor.title') || 'Distributors'}
            </button>
            <span className="breadcrumb-separator">&gt;</span>
            <span className="breadcrumb-current">{distributor ? 'Edit Distributor' : t('distributor.breadcrumb')}</span>
          </nav>

          {/* Title Header Section */}
          <div className="add-song-header-row">
            <div className="header-icon-box">
              {/* Globe Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </div>
            <div className="header-text-container">
              <h1 className="add-song-title">{distributor ? 'Edit Distribution Account' : t('distributor.addNewTitle')}</h1>
              <p className="add-song-subtitle">{distributor ? 'Update details of the selected distribution account' : t('distributor.addNewSubtitle')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Form Fields Container */}
      <form onSubmit={handleSubmit} className="distribution-form">
        <div className="form-card">
          {/* Email Input */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('distributor.emailLabel')} <span className="required-asterisk">*</span>
            </label>
            <input
              type="email"
              className="form-text-input"
              placeholder={t('distributor.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {/* Company Input */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('distributor.companyLabel')} <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              className="form-text-input"
              placeholder={t('distributor.companyPlaceholder')}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {/* Percentage Input */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('distributor.percentageLabel')} <span className="required-asterisk">*</span>
            </label>
            <div className="percentage-input-wrapper">
              <input
                type="text"
                className="form-text-input"
                placeholder={t('distributor.percentagePlaceholder')}
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                disabled={isSubmitting}
              />
              <span className="percentage-symbol">%</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="distribution-form-actions">
          {isModal ? (
            <>
              <button
                type="button"
                className="form-btn-outlined"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="form-btn-primary"
                disabled={isSubmitting}
              >
                {/* Save Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>{t('common.save')}</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="form-btn-outlined"
                onClick={distributor ? onCancel : handleReset}
                disabled={isSubmitting}
              >
                {distributor ? t('common.cancel') : t('common.reset')}
              </button>
              <button
                type="submit"
                className="form-btn-primary"
                disabled={isSubmitting}
              >
                {/* Save Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>{t('common.save')}</span>
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default DistributionForm;
